/* eslint-disable dxp-rules/no-dialog-from-sparky */
import { useState } from 'react';

import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import { postEnecoBeXapiSiteApiV1AccountsByAccountNumberLinkOwner } from '@eneco/dc-be/client';
import { unwrapNotFoundError, unwrapValidationError } from '@eneco/dc-be/utils/unwrapError';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { Box, Button, Dialog, Form, InputText, NotificationBox, Stack } from '@eneco/sparky';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { AccountLinkDialogRendering } from '@sitecore/types/AccountLinkDialog';
import { signIn } from 'next-auth/react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useAccountLinkDialog } from '../../providers/AccountLinkDialogProvider';
import { InputDigits } from '../InputDigits';

type FormValues = { alias: string; lastName: string; accountNumber: string };

export const AccountLinkDialog = () => {
  const [isLoading, setLoading] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [isOpen, setIsOpen] = useAccountLinkDialog();

  const { switchAccount } = useSelfServiceAccount();
  const { fields } = useContent<AccountLinkDialogRendering>();
  const { trackEventBE } = useTracking();
  const { format } = useFormatter();
  const [error, setError] = useState<NotificationBoxProps | null>(null);

  const accountLinkSchema = yup.object({
    alias: yup
      .string()
      .required(fields.aliasFormField.value.requiredMessage)
      .min(1, fields.aliasFormField.value.requiredMessage)
      .max(25, fields.aliasFormField.value.validationMessage),
    lastName: yup.string().required(fields.lastNameFormField.value.requiredMessage),
    accountNumber: yup
      .string()
      .required(fields.clientNumberFormField.value.requiredMessage)
      .matches(/^545/, fields.clientNumberFormField.value.validationMessage),
  });

  const resolver = typedYupResolver<FormValues>(accountLinkSchema);

  const {
    formState: { errors },
    handleSubmit,
    register,
    setFocus,
    clearErrors,
    reset,
  } = useForm<FormValues>({
    resolver,
  });

  const submitForm: SubmitHandler<FormValues> = async ({ alias, lastName, accountNumber }) => {
    clearErrors('root');
    setError(null);
    setLoading(true);
    await postEnecoBeXapiSiteApiV1AccountsByAccountNumberLinkOwner({
      body: {
        lastName,
        isOwnerReassignmentFlow: false,
        alias,
      },
      path: {
        accountNumber,
      },
    })
      .then(result => {
        const { response, error } = result;
        if (response.ok) {
          setSuccess(true);
          reset();
          trackEventBE('account_link_submit', { status: 'success' });
          switchAccount({ crmAccountNumber: accountNumber });
          return;
        }

        //errors
        setLoading(false);
        const validationErrors = unwrapValidationError(error);
        const notFoundErrors = unwrapNotFoundError(error);

        if (validationErrors.includes('AccountLinkingRequest.TargetCrmAccount.AlreadyLinked')) {
          setError({
            text: <RichText html={fields.accountAlreadyLinkedToAnotherUserNotification?.value.content} />,
            title: fields.accountAlreadyLinkedToAnotherUserNotification?.value.title,
            variant: 'error',
            isAlert: true,
          });
          trackEventBE('account_link_submit', { status: 'failed', message: 'crm account already linked' });
        } else if (validationErrors.includes('AccountLinkingRequest.TargetCrmAccount.User.AlreadyLinked')) {
          setError({
            text: <RichText html={fields.accountAlreadyLinkedToCurrentUserNotification?.value.content} />,
            title: fields.accountAlreadyLinkedToCurrentUserNotification?.value.title,
            variant: 'error',
            isAlert: true,
          });
          trackEventBE('account_link_submit', { status: 'failed', message: 'user account already linked' });
        } else if (validationErrors.includes('CurrentUser.AccountLink.AliasTooLong')) {
          setError({
            text: <RichText html={fields.aliasFormField.value.validationMessage} />,
            variant: 'error',
            isAlert: true,
          });
          trackEventBE('account_link_submit', { status: 'failed', message: 'alias too long' });
        } else if (
          notFoundErrors.includes('CurrentUser.Id.NotFound') ||
          notFoundErrors.includes('AccountLinkingRequest.TargetCrmAccount.NotFound') ||
          validationErrors.includes('AccountLinkingRequest.TargetCrmAccount.LastName.Mismatch')
        ) {
          setError({
            title: fields.invalidCombinationNotification?.value.title,
            text: <RichText html={fields.invalidCombinationNotification?.value.content} />,
            variant: 'error',
            isAlert: true,
          });
          setFocus('accountNumber');
          trackEventBE('account_link_submit', { status: 'failed', message: 'unexpected error occurred' });
        } else if (validationErrors.includes('AccountLinkingRequest.TargetCrmAccount.AlreadyOwner')) {
          setError({
            text: (
              <RichText
                html={format(fields.accountAlreadyHasOwnerNotfication?.value.content, {
                  email: (error?.errors?.metadata as { OwnerEmail: string })?.OwnerEmail,
                })}
              />
            ),
            title: fields.accountAlreadyHasOwnerNotfication?.value.title,
            variant: 'error',
            isAlert: true,
          });
        } else if (validationErrors.length || notFoundErrors.length || !response.ok) {
          throw new Error('unexpected behaviour');
        }
      })
      .catch(e => {
        void e;
        setLoading(false);
        setError({
          text: <RichText html={fields.generalErrorNotification.value.content} />,
          title: fields.generalErrorNotification.value.title,
          variant: 'error',
          isAlert: true,
        });
        trackEventBE('account_link_submit', { status: 'failed', message: 'unexpected error occurred' });
      });
  };

  return (
    <Dialog
      setOpen={open => {
        setIsOpen(open);
        if (open) setSuccess(false);
      }}
      isOpen={isOpen}
      onClose={() => {
        if (isSuccess) {
          signIn('okta', { callbackUrl: fields.successDialogOktaRedirectLink.value.href });
        }
      }}
      title={fields.title.value}
      width="regular">
      {!isSuccess ? (
        <Form onSubmit={handleSubmit(submitForm)}>
          <Box paddingBottom="6">
            <Stack gap={6}>
              <InputText
                label={fields.aliasFormField.value.label}
                error={errors?.alias?.message}
                {...register('alias')}
              />
              <InputText
                label={fields.lastNameFormField.value.label}
                error={errors?.lastName?.message}
                {...register('lastName')}
              />
              <InputDigits
                label={fields.clientNumberFormField.value.label}
                error={errors?.accountNumber?.message}
                {...register('accountNumber')}
              />
              {error && <NotificationBox isAlert variant="error" text={error.text} title={error.title} />}
            </Stack>
          </Box>
          <Stack direction="row" gap={6}>
            <Button type="submit" isLoading={isLoading}>
              {fields.submitButtonText.value}
            </Button>
            <Dialog.Close asChild>
              <Button action="secondary">{fields.cancelButtonText.value}</Button>
            </Dialog.Close>
          </Stack>
        </Form>
      ) : (
        <Box paddingTop={'6'}>
          <NotificationBox
            isAlert
            variant="success"
            title={fields.successMessageNotification?.value.title}
            text={<RichText html={fields.successMessageNotification?.value.content} />}
          />
          <Box paddingTop={6}>
            <Dialog.Close asChild>
              <Button action="primary">{fields.successDialogActionButtonText.value}</Button>
            </Dialog.Close>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default AccountLinkDialog;
